<template>

  <main class="video-detail">

    <!-- لودینگ -->
    <div v-if="loading" class="state-box">در حال بارگذاری ویدئو…</div>

    <!-- پیدا نشد -->
    <div v-else-if="!video" class="state-box">
      <p>این ویدئو پیدا نشد.</p>
      <router-link class="back-link" to="/videos">بازگشت به آرشیو ویدئوها</router-link>
    </div>

    <template v-else>

      <!-- پخش‌کنندهٔ سبک -->
      <LiteVideoPlayer
          :provider="video.provider"
          :external-id="video.externalId"
          :thumbnail-url="video.thumbnailUrl || ''"
          :title="video.title"
      />

      <!-- مشخصات -->
      <section class="head">
        <h1>{{ video.title }}</h1>
        <div class="meta">
          <span v-if="video.publishedAt">🗓 {{ faDate(video.publishedAt) }}</span>
          <span v-if="video.views">👁 {{ video.views.toLocaleString('fa-IR') }} بازدید</span>
          <span v-if="durationLabel">⏱ {{ durationLabel }}</span>
        </div>
      </section>

      <!-- توضیحات -->
      <section v-if="video.description" class="description">
        <p>{{ video.description }}</p>
      </section>

      <!-- تگ‌ها -->
      <nav v-if="video.tags && video.tags.length" class="tag-row" aria-label="برچسب‌ها">
        <router-link
            v-for="t in video.tags"
            :key="t"
            class="chip"
            :to="`/videos/tag/${encodeURIComponent(t)}`"
        >#{{ t }}</router-link>
      </nav>

      <!-- منبع اصلی — شفافیت حقوقی -->
      <p v-if="video.sourceUrl" class="source">
        منبع:
        <a :href="video.sourceUrl" target="_blank" rel="noopener">صفحهٔ اصلی ویدئو ↗</a>
      </p>

      <!-- مرتبط‌ها -->
      <section v-if="related.length" class="related">
        <h2>ویدئوهای مرتبط</h2>
        <div class="rgrid">
          <router-link
              v-for="r in related"
              :key="r.id"
              class="ritem"
              :to="`/video/${r.slug}`"
          >
            <img v-if="r.thumbnailUrl" :src="r.thumbnailUrl" :alt="r.title" loading="lazy">
            <div v-else class="rnoimg">▶</div>
            <span class="rtitle">{{ r.title }}</span>
          </router-link>
        </div>
      </section>

      <p class="back-row">
        <router-link class="back-link" to="/videos">آرشیو ویدئوها</router-link>
        —
        <router-link class="back-link" to="/special">رویدادهای ویژه</router-link>
      </p>

    </template>

  </main>

</template>

<script>
import api from '@/services/api'
import { setSeo } from '@/utils/seo'
import LiteVideoPlayer from '@/components/LiteVideoPlayer.vue'

export default {

  name: 'VideoDetail',

  components: { LiteVideoPlayer },

  data() {
    return {
      video: null,
      related: [],
      loading: true,
    }
  },

  computed: {
    durationLabel() {
      if (!this.video || !this.video.durationSec) return ''
      const m = Math.floor(this.video.durationSec / 60)
      const s = String(this.video.durationSec % 60).padStart(2, '0')
      return `${m}:${s}`
    },
  },

  watch: {
    '$route.params.slug'() {
      this.load()
    },
  },

  async created() {
    await this.load()
  },

  methods: {
    faDate(d) {
      return new Date(d).toLocaleDateString('fa-IR')
    },

    async load() {
      this.loading = true
      this.video = null
      this.related = []

      const slug = this.$route.params.slug

      try {
        const res = await api.request(`/videos/${encodeURIComponent(slug)}`)

        if (!res || !res.video) {
          this.video = null
          // 404 واقعی — برای گوگل noindex
          if (api.lastStatus === 404) {
            setSeo({ title: 'ویدئو پیدا نشد | ضربه آزاد', noindex: true })
          }
          return
        }

        this.video = res.video
        this.related = res.related || []

        setSeo({
          title: `${this.video.title} | ویدئو | ضربه آزاد`,
          description: this.video.description || `${this.video.title} — تماشای کلیپ در آرشیو ویدئوهای ضربه آزاد`,
          image: this.video.thumbnailUrl || null,
          path: `/video/${this.video.slug}`,
        })
      } catch (e) {
        console.error('VideoDetail:', e)
        this.video = null
      } finally {
        this.loading = false
      }
    },
  },

}

</script>

<style scoped lang="scss">

.video-detail {
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
  padding: 20px 16px 40px;
  box-sizing: border-box;
}

.state-box {
  padding: 48px 16px;
  text-align: center;
  color: #64748b;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, .05);
}

.back-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 600;
}

.head {
  margin: 16px 0 12px;

  h1 {
    margin: 0 0 8px;
    font-size: 1.4rem;
    line-height: 1.7;
    color: #0f172a;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    color: #64748b;
    font-size: 0.85rem;
  }
}

.description {
  background: #fff;
  border-radius: 16px;
  padding: 14px 18px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, .05);
  margin-bottom: 14px;

  p {
    margin: 0;
    color: #334155;
    line-height: 1.9;
    white-space: pre-line;
  }
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;

  .chip {
    padding: 5px 12px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #0f766e;
    font-size: 0.8rem;
    font-weight: 600;
    text-decoration: none;
  }
}

.source {
  margin: 0 0 18px;
  color: #64748b;
  font-size: 0.85rem;

  a {
    color: #0f766e;
  }
}

.related {
  margin-top: 22px;

  h2 {
    font-size: 1.1rem;
    color: #0f172a;
    margin: 0 0 12px;
  }
}

.rgrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.ritem {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0 6px 16px rgba(0, 0, 0, .05);
  transition: transform .2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  img,
  .rnoimg {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    display: block;
  }

  .rnoimg {
    background: linear-gradient(135deg, #022c22, #064e3b);
    color: rgba(255, 255, 255, .8);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
  }

  .rtitle {
    display: block;
    padding: 8px 10px 10px;
    color: #0f172a;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.6;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.back-row {
  margin-top: 20px;
  text-align: center;
  color: #94a3b8;
}

@media (max-width: 768px) {
  .video-detail {
    padding: 14px 12px 32px;
  }

  .rgrid {
    grid-template-columns: repeat(2, 1fr);
  }
}

</style>
